import { useState } from "react";
import { MessageSquare, CheckCircle2, Reply } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import type { ReviewComment } from "@/api/protocol-types";
import { MentionInput } from "../MentionInput";

interface MarginComment extends ReviewComment {
  textRange?: { from: number; to: number; text: string };
}

interface MarginCommentPanelProps {
  comments: MarginComment[];
  activeCommentId: string | null;
  onClickComment: (commentId: string) => void;
  onResolve: (commentId: string) => void;
  onReply: (commentId: string, content: string, mentionedUids: string[]) => void;
}

export function MarginCommentPanel({
  comments,
  activeCommentId,
  onClickComment,
  onResolve,
  onReply,
}: MarginCommentPanelProps) {
  const uid = useAuthStore((s) => s.uid);

  if (comments.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <div className="text-center">
          <MessageSquare className="mx-auto h-6 w-6 text-muted-foreground/40" />
          <p className="mt-2 text-[11px] text-muted-foreground">
            No comments yet. Select text and click Comment to start.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-2">
      {comments.map((comment) => (
        <MarginCommentBubble
          key={comment.id}
          comment={comment}
          isActive={comment.id === activeCommentId}
          isAuthor={comment.authorUid === uid}
          onClick={() => onClickComment(comment.id)}
          onResolve={() => onResolve(comment.id)}
          onReply={(content, mentionedUids) =>
            onReply(comment.id, content, mentionedUids)
          }
        />
      ))}
    </div>
  );
}

function MarginCommentBubble({
  comment,
  isActive,
  isAuthor,
  onClick,
  onResolve,
  onReply,
}: {
  comment: MarginComment;
  isActive: boolean;
  isAuthor: boolean;
  onClick: () => void;
  onResolve: () => void;
  onReply: (content: string, mentionedUids: string[]) => void;
}) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");

  const handleReplySubmit = (mentionedUids: string[]) => {
    if (!replyText.trim()) return;
    onReply(replyText.trim(), mentionedUids);
    setReplyText("");
    setShowReply(false);
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full rounded-lg border p-2.5 text-left transition-all",
        isActive
          ? "border-primary bg-primary/5 shadow-sm"
          : "border-border bg-card hover:border-primary/30",
        comment.resolved && "opacity-50",
      )}
    >
      {/* Quoted text */}
      {comment.textRange?.text && (
        <div className="mb-1.5 rounded border-l-2 border-amber-400 bg-amber-50 px-2 py-1 text-[9px] italic text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 line-clamp-2">
          {comment.textRange.text}
        </div>
      )}

      {/* Author row */}
      <div className="flex items-center gap-1">
        <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[7px] font-bold text-muted-foreground">
          {comment.authorName
            .split(" ")
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase()}
        </div>
        <span className="text-[10px] font-medium text-foreground">
          {comment.authorName}
        </span>
        <span className="text-[9px] text-muted-foreground">
          {formatTimeAgo(comment.createdAt)}
        </span>
      </div>

      {/* Content */}
      <p
        className={cn(
          "mt-1 text-[11px] text-foreground",
          comment.resolved && "line-through",
        )}
      >
        {comment.content}
      </p>

      {/* Actions */}
      {!comment.resolved && (
        <div className="mt-1.5 flex items-center gap-2">
          <span
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowReply(!showReply);
            }}
            className="flex items-center gap-0.5 text-[9px] text-muted-foreground hover:text-foreground"
          >
            <Reply className="h-2.5 w-2.5" />
            Reply
          </span>
          {isAuthor && (
            <span
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                onResolve();
              }}
              className="flex items-center gap-0.5 text-[9px] text-muted-foreground hover:text-emerald-600"
            >
              <CheckCircle2 className="h-2.5 w-2.5" />
              Resolve
            </span>
          )}
        </div>
      )}
      {comment.resolved && (
        <span className="mt-1 flex items-center gap-0.5 text-[9px] text-emerald-600">
          <CheckCircle2 className="h-2.5 w-2.5" />
          Resolved
        </span>
      )}

      {/* Reply input */}
      {showReply && (
        <div
          className="mt-2 flex gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <MentionInput
            value={replyText}
            onChange={setReplyText}
            onSubmit={handleReplySubmit}
            placeholder="Reply..."
            autoFocus
            className="flex-1 rounded border border-border bg-background px-2 py-1 text-[10px] text-foreground placeholder:text-muted-foreground"
          />
        </div>
      )}
    </button>
  );
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString();
}
